import React, { useCallback, useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {
  cancelPoolParty,
  checkInPoolPartyGuest,
  createPoolParty,
  deletePoolParty,
  getMyPoolParties,
  getPoolPartyGuests,
  PartnerPoolParty,
  partyPrice,
  partyWhen,
  POOL_TYPES,
  PoolPartyGuest,
  PoolPartyInput,
  savePoolParty
} from '../../features/poolParties/api';
import { errorMessage, formatDateTime, Notice, NoticeBox, PageHead, StatusBadge, Toggle, usePortal } from './shared';
import { useFeatures } from '../../features';

type Form = Record<'title' | 'description' | 'poolType' | 'eventDate' | 'startTime' | 'endTime' | 'capacity' | 'pricePerGuest' | 'maxGuestsPerBooking'
  | 'inclusions' | 'agePolicy' | 'dressCode' | 'safetyRules' | 'cancellationPolicy' | 'emergencyContact', string> & { isPublished: boolean };

const EMPTY: Form = {
  title: '', description: '', poolType: POOL_TYPES[0], eventDate: '', startTime: '14:00', endTime: '20:00', capacity: '50', pricePerGuest: '0',
  maxGuestsPerBooking: '6', inclusions: 'Pool access, DJ / music', agePolicy: '21+ only. Valid photo ID checked at entry.', dressCode: 'Swimwear in the pool area',
  safetyRules: 'Lifeguard on duty. No glass near the pool. No swimming after drinking.', cancellationPolicy: 'Free cancellation until the party starts.',
  emergencyContact: '', isPublished: true
};

const toForm = (p: PartnerPoolParty): Form => ({
  title: p.title, description: p.description, poolType: p.poolType, eventDate: p.eventDate, startTime: p.startTime.slice(0, 5), endTime: p.endTime.slice(0, 5),
  capacity: String(p.capacity), pricePerGuest: String(p.pricePerGuest), maxGuestsPerBooking: String(p.maxGuestsPerBooking),
  inclusions: p.inclusions ?? '', agePolicy: p.agePolicy ?? '', dressCode: p.dressCode ?? '', safetyRules: p.safetyRules ?? '',
  cancellationPolicy: p.cancellationPolicy ?? '', emergencyContact: p.emergencyContact ?? '', isPublished: p.isPublished
});

const toInput = (f: Form): PoolPartyInput => ({
  title: f.title, description: f.description, poolType: f.poolType, eventDate: f.eventDate,
  startTime: f.startTime.length === 5 ? `${f.startTime}:00` : f.startTime, endTime: f.endTime.length === 5 ? `${f.endTime}:00` : f.endTime,
  capacity: Number(f.capacity) || 0, pricePerGuest: Number(f.pricePerGuest) || 0, maxGuestsPerBooking: Number(f.maxGuestsPerBooking) || 0,
  inclusions: f.inclusions || null, agePolicy: f.agePolicy || null, dressCode: f.dressCode || null, safetyRules: f.safetyRules || null,
  cancellationPolicy: f.cancellationPolicy || null, emergencyContact: f.emergencyContact || null, isPublished: f.isPublished
});

const STATUS_LABEL: Record<string, string> = { SoldOut: 'Sold Out', Pending: 'In Review' };

function Editor({ party, onClose, onSaved }: { party: PartnerPoolParty | null; onClose: () => void; onSaved: (text: string) => void }) {
  const [form, setForm] = useState<Form>(party ? toForm(party) : EMPTY);
  const [notice, setNotice] = useState<Notice>(null);
  const [saving, setSaving] = useState(false);
  const set = (key: keyof Form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setForm({ ...form, [key]: e.target.value });

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setNotice(null);
    try {
      const saved = party ? await savePoolParty(party.id, toInput(form)) : await createPoolParty(toInput(form));
      onSaved(saved.status === 'Pending'
        ? `"${saved.title}" saved and sent for review. It appears on Cheers2Night once approved (usually within a day).`
        : saved.status === 'Draft' ? `"${saved.title}" saved as a draft.` : `"${saved.title}" saved.`);
    } catch (err) {
      setNotice({ severity: 'error', text: errorMessage(err, 'Could not save the pool party.') });
      setSaving(false);
    }
  };

  const field = (key: keyof Form, label: string, props: React.InputHTMLAttributes<HTMLInputElement> & { required?: boolean } = {}) => (
    <div className="pp-field">
      <label htmlFor={`pool-${key}`}>{label}{props.required && <span className="pp-req">*</span>}</label>
      <input id={`pool-${key}`} className="pp-input" value={form[key] as string} onChange={set(key)} {...props} required={false} />
    </div>
  );
  const area = (key: keyof Form, label: string, max: number, required = false) => (
    <div className="pp-field">
      <label htmlFor={`pool-${key}`}>{label}{required && <span className="pp-req">*</span>}</label>
      <textarea id={`pool-${key}`} className="pp-input" style={{ minHeight: 70 }} maxLength={max} value={form[key] as string} onChange={set(key)} />
    </div>
  );

  return (
    <form className="pp-editor" onSubmit={save} aria-label={party ? 'Edit pool party' : 'New pool party'} style={{ marginBottom: 18 }}>
      <div className="pp-editor-head">
        <h3>{party ? 'Edit Pool Party' : 'New Pool Party'}</h3>
        <button type="button" className="pp-icon-btn" onClick={onClose} aria-label="Close"><CloseIcon /></button>
      </div>
      <div style={{ padding: notice ? '14px 20px 0' : 0 }}><NoticeBox notice={notice} /></div>
      {party && party.bookedGuests > 0 && (
        <div style={{ padding: '14px 20px 0' }}><div className="pp-notice info">{party.bookedGuests} guest(s) have booked. Changing the date or time notifies them by SMS and in the app.</div></div>
      )}
      <div className="pp-editor-cols">
        <div className="pp-editor-col">
          {field('title', 'Party Name', { required: true, maxLength: 120, placeholder: 'Sunday Pool Splash' })}
          <div className="pp-field">
            <label htmlFor="pool-poolType">Pool Type<span className="pp-req">*</span></label>
            <select id="pool-poolType" className="pp-input" value={form.poolType} onChange={set('poolType')}>{POOL_TYPES.map((t) => <option key={t}>{t}</option>)}</select>
          </div>
          {area('description', 'Description', 1000, true)}
          {area('inclusions', "What's Included", 500)}
          {area('agePolicy', 'Age Policy', 300, true)}
          {area('dressCode', 'Dress Code', 300)}
        </div>
        <div className="pp-editor-col">
          {field('eventDate', 'Date', { required: true, type: 'date' })}
          <div className="pp-row">
            {field('startTime', 'Start Time', { required: true, type: 'time' })}
            {field('endTime', 'End Time', { required: true, type: 'time' })}
          </div>
          <div className="pp-row">
            {field('capacity', 'Capacity (guests)', { required: true, type: 'number', min: 1, max: 5000 })}
            {field('maxGuestsPerBooking', 'Max Guests per Booking', { required: true, type: 'number', min: 1, max: 20 })}
          </div>
          <div className="pp-field">
            <label htmlFor="pool-price">Price per Guest (paid at venue)</label>
            <div className="pp-prefix"><span>₹</span><input id="pool-price" type="number" min={0} className="pp-input" value={form.pricePerGuest} onChange={set('pricePerGuest')} /></div>
          </div>
          {area('safetyRules', 'Safety Rules', 500, true)}
          {area('cancellationPolicy', 'Cancellation Policy', 500)}
          {field('emergencyContact', 'Emergency Contact Number', { required: true, maxLength: 120, inputMode: 'tel', placeholder: '+91 98xxxxxxxx' })}
          <div className="pp-toggles">
            <Toggle checked={form.isPublished} onChange={(v) => setForm({ ...form, isPublished: v })} label="Publish (send for review)" />
          </div>
        </div>
      </div>
      <div className="pp-editor-foot">
        <button type="button" className="pp-btn pp-outline" onClick={onClose}>Cancel</button>
        <button type="submit" className="pp-btn pp-gold" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
      </div>
    </form>
  );
}

function GuestList({ party, onClose }: { party: PartnerPoolParty; onClose: () => void }) {
  const [guests, setGuests] = useState<PoolPartyGuest[] | null>(null);
  useEffect(() => {
    getPoolPartyGuests(party.id).then(setGuests).catch(() => setGuests([]));
  }, [party.id, party.checkedInGuests]);
  return (
    <section className="pp-panel" style={{ marginBottom: 18 }}>
      <div className="pp-strip-head">
        <div><h3>Guest List: {party.title}</h3><small>{partyWhen(party)} · {party.bookedGuests}/{party.capacity} booked · {party.checkedInGuests} checked in</small></div>
        <button type="button" className="pp-icon-btn" onClick={onClose} aria-label="Close guest list"><CloseIcon /></button>
      </div>
      {guests === null ? <p className="pp-muted">Loading...</p> : guests.length === 0 ? <p className="pp-muted">No bookings yet.</p> : (
        <div className="pp-table-wrap">
          <table className="pp-table">
            <thead><tr><th>Guest</th><th>Mobile</th><th className="num">Guests</th><th>Booked</th><th>Checked In</th><th className="num">Status</th></tr></thead>
            <tbody>
              {guests.map((g) => (
                <tr key={g.bookingId}>
                  <td>{g.displayName}</td><td>{g.mobile}</td><td className="num">{g.guests}</td>
                  <td>{formatDateTime(g.bookedAt)}</td><td>{g.checkedInAt ? formatDateTime(g.checkedInAt) : '–'}</td>
                  <td className="num"><StatusBadge status={g.status === 'CheckedIn' ? 'Redeemed' : g.status === 'Confirmed' ? 'Confirmed' : 'Expired'} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="pp-hint">Customer numbers are partly hidden for privacy.</p>
        </div>
      )}
    </section>
  );
}

// Partner portal → Pool Parties: create and manage parties (reviewed by Cheers2Night before they go
// live), see the guest list, and check guests in at the door with their entry code.
const PoolPartiesPage: React.FC = () => {
  const { refreshDashboard } = usePortal();
  // Bookings, guest lists and check-in are off while Cheers2Night is discovery-only.
  const { poolPartyBookings: bookings } = useFeatures();
  const [parties, setParties] = useState<PartnerPoolParty[] | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const [editing, setEditing] = useState<PartnerPoolParty | 'new' | null>(null);
  const [viewing, setViewing] = useState<PartnerPoolParty | null>(null);
  const [code, setCode] = useState('');
  const [checkInNotice, setCheckInNotice] = useState<Notice>(null);
  const [checking, setChecking] = useState(false);

  const load = useCallback(() => getMyPoolParties()
    .then((list) => {
      setParties(list);
      setViewing((v) => (v ? list.find((p) => p.id === v.id) ?? null : null));
    })
    .catch(() => setNotice({ severity: 'error', text: 'Could not load your pool parties. Please refresh.' })), []);
  useEffect(() => {
    load();
  }, [load]);

  const checkIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setChecking(true);
    setCheckInNotice(null);
    try {
      const r = await checkInPoolPartyGuest(code);
      setCheckInNotice({ severity: 'success', text: `Checked in: ${r.guestName}, ${r.guests === 1 ? '1 guest' : `${r.guests} guests`} for ${r.partyTitle}. Check their photo ID.` });
      setCode('');
      load();
      refreshDashboard();
    } catch (err) {
      setCheckInNotice({ severity: 'error', text: errorMessage(err, 'Could not check this code.') });
    } finally {
      setChecking(false);
    }
  };

  const cancel = async (p: PartnerPoolParty) => {
    const reason = window.prompt(`Cancel "${p.title}"? ${p.bookedGuests > 0 ? `All ${p.bookedGuests} booked guest(s) are told by SMS. ` : ''}Optional reason (shown to guests):`, '');
    if (reason === null) return;
    try {
      await cancelPoolParty(p.id, reason);
      setNotice({ severity: 'success', text: `"${p.title}" cancelled.${p.bookedGuests > 0 ? ' Booked guests have been notified.' : ''}` });
      load();
    } catch (err) {
      setNotice({ severity: 'error', text: errorMessage(err, 'Could not cancel the pool party.') });
    }
  };

  const remove = async (p: PartnerPoolParty) => {
    if (!window.confirm(`Delete "${p.title}"? This can't be undone.`)) return;
    try {
      await deletePoolParty(p.id);
      setNotice({ severity: 'success', text: `"${p.title}" deleted.` });
      if (viewing?.id === p.id) setViewing(null);
      load();
    } catch (err) {
      setNotice({ severity: 'error', text: errorMessage(err, 'Could not delete the pool party.') });
    }
  };

  const closed = (p: PartnerPoolParty) => p.status === 'Cancelled' || p.status === 'Completed';

  return (
    <>
      <PageHead title="Pool Parties" subtitle={bookings ? 'List pool parties, track bookings and check guests in at the door.' : 'List your pool parties so customers nearby can find them. Entry is handled at your venue.'}>
        <button type="button" className="pp-btn pp-gold" onClick={() => setEditing('new')}><AddIcon />New Pool Party</button>
      </PageHead>
      <NoticeBox notice={notice} />

      {bookings && <form className="pp-panel" onSubmit={checkIn} style={{ marginBottom: 18 }}>
        <h3 style={{ marginTop: 0 }}>Check In a Guest</h3>
        <p className="pp-muted" style={{ marginTop: -6 }}>Enter the 8-character entry code from the guest's booking. Each code works once.</p>
        <div className="pp-narrow">
          <NoticeBox notice={checkInNotice} />
          <div className="pp-field">
            <label htmlFor="pool-checkin-code">Entry Code<span className="pp-req">*</span></label>
            <input id="pool-checkin-code" className="pp-input" required autoComplete="off" placeholder="ABCD-EFGH" value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())} style={{ letterSpacing: 2, fontFamily: 'monospace', fontSize: 18 }} />
          </div>
          <div><button type="submit" className="pp-btn pp-gold" disabled={checking || code.replace(/[^A-Z0-9]/g, '').length !== 8}>{checking ? 'Checking...' : 'Verify & Check In'}</button></div>
        </div>
      </form>}

      {editing !== null && (
        <Editor
          key={editing === 'new' ? 'new' : editing.id}
          party={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(text) => { setEditing(null); setNotice({ severity: 'success', text }); load(); }}
        />
      )}
      {bookings && viewing && <GuestList party={viewing} onClose={() => setViewing(null)} />}

      {parties === null ? (
        <p className="pp-muted">Loading pool parties...</p>
      ) : parties.length === 0 ? (
        <div className="pp-panel pp-empty">
          <h3>No pool parties yet</h3>
          <p className="pp-muted">Create one and our team reviews it before it goes live, usually within a day. Booking is free for guests; they pay you at the venue.</p>
          <button type="button" className="pp-btn pp-gold" onClick={() => setEditing('new')}><AddIcon />New Pool Party</button>
        </div>
      ) : (
        <section className="pp-panel">
          <div className="pp-table-wrap">
            <table className="pp-table">
              <thead><tr><th>Party</th><th>When</th><th className="num">Booked</th><th>Status</th><th className="num">Actions</th></tr></thead>
              <tbody>
                {parties.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <b>{p.title}</b>
                      <div className="pp-muted" style={{ fontSize: 12 }}>{p.poolType} · {partyPrice(p.pricePerGuest)}</div>
                      {p.moderationNote && (p.status === 'Rejected' || p.status === 'Suspended') && (
                        <div className="pp-notice error" style={{ margin: '6px 0 0', padding: '6px 9px', fontSize: 12 }}>Review note: {p.moderationNote}</div>
                      )}
                      {p.status === 'Cancelled' && p.cancelReason && <div className="pp-muted" style={{ fontSize: 12 }}>Cancelled: {p.cancelReason}</div>}
                    </td>
                    <td>{partyWhen(p)}</td>
                    <td className="num">{p.bookedGuests}/{p.capacity}{p.checkedInGuests > 0 && <div className="pp-muted" style={{ fontSize: 12 }}>{p.checkedInGuests} in</div>}</td>
                    <td><StatusBadge status={STATUS_LABEL[p.status] ?? p.status} /></td>
                    <td className="num" style={{ whiteSpace: 'nowrap' }}>
                      {bookings && <><button type="button" className="pp-btn pp-outline pp-sm" onClick={() => setViewing(p)}>Guests</button>{' '}</>}
                      {!closed(p) && <button type="button" className="pp-btn pp-outline pp-sm" onClick={() => setEditing(p)}>Edit</button>}{' '}
                      {!closed(p) && <button type="button" className="pp-btn pp-outline pp-sm" onClick={() => cancel(p)}>Cancel</button>}{' '}
                      {p.bookedGuests === 0 && p.status !== 'Cancelled' && <button type="button" className="pp-btn pp-outline pp-sm pp-danger" onClick={() => remove(p)}>Delete</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="pp-hint">New parties and changes to what guests read are reviewed by Cheers2Night before they show on the site. Date and capacity changes apply straight away.</p>
        </section>
      )}
    </>
  );
};

export default PoolPartiesPage;
